import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";

export default function GlassCardHero({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryColor = content.colorPalette?.primary || "#3B82F6";
    const secondaryColor = content.colorPalette?.secondary || "#4F46E5";
    const fontFamily = content.fontFamily || "'Inter', sans-serif";
    const padding = content.padding || "normal";
    const animation = content.animation || "none";

    const getPaddingClass = () => {
        if (padding === "compact") return "py-16 md:py-24";
        if (padding === "spacious") return "py-32 md:py-48";
        return "py-20 md:py-32";
    };

    const getAnimationClass = () => {
        if (animation === "fade") return "animate-[fade-in_1s_ease-out]";
        if (animation === "slide") return "animate-[slide-up_1s_ease-out]";
        if (animation === "zoom") return "animate-[zoom-in_1s_ease-out]";
        return "";
    };

    return (
        <div
            className={`relative overflow-hidden px-8 ${getPaddingClass()} ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            <style>{`
                @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                @keyframes zoom-in { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                @keyframes glow { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
            `}</style>

            {/* Dark Gradient Background */}
            <div
                className="absolute inset-0 z-0"
                style={{
                    background: `linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #0f172a 100%)`,
                }}
            />

            {/* Mesh gradient overlay */}
            <div
                className="absolute inset-0 z-0 opacity-60"
                style={{
                    background: `radial-gradient(ellipse at 20% 20%, ${primaryColor}30 0%, transparent 50%),
                                    radial-gradient(ellipse at 80% 80%, ${secondaryColor}25 0%, transparent 50%),
                                    radial-gradient(ellipse at 50% 50%, ${primaryColor}15 0%, transparent 60%)`,
                }}
            />

            {/* Subtle image texture */}
            {(content.image || content.imageUrl) && (
                <div className="absolute inset-0 z-0 opacity-[0.08]">
                    <img
                        src={content.image || content.imageUrl}
                        alt=""
                        className="w-full h-full object-cover blur-2xl scale-110"
                    />
                </div>
            )}

            {/* Floating neon orbs */}
            <div
                className="absolute top-20 left-20 w-64 h-64 rounded-full blur-3xl opacity-60 pointer-events-none"
                style={{
                    background: `radial-gradient(circle, ${primaryColor}60, transparent)`,
                }}
            />
            <div
                className="absolute bottom-20 right-20 w-80 h-80 rounded-full blur-3xl opacity-50 pointer-events-none"
                style={{
                    background: `radial-gradient(circle, ${secondaryColor}50, transparent)`,
                }}
            />
            <div
                className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full blur-2xl opacity-40 pointer-events-none"
                style={{
                    background: `radial-gradient(circle, ${primaryColor}40, transparent)`,
                }}
            />

            {/* Grid pattern */}
            <div
                className="absolute inset-0 z-0 opacity-[0.04]"
                style={{
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                    backgroundSize: "40px 40px",
                }}
            />

            {/* Dark Glass Card Container */}
            <div
                className={`relative z-10 flex items-center justify-center ${getAnimationClass()}`}
            >
                <div
                    className="relative max-w-2xl w-full p-12 rounded-3xl text-center"
                    style={{
                        background: "rgba(15, 23, 42, 0.75)",
                        backdropFilter: "blur(24px)",
                        WebkitBackdropFilter: "blur(24px)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        boxShadow: `0 25px 60px -15px rgba(0, 0, 0, 0.5), 
                                    0 0 0 1px rgba(255,255,255,0.05) inset,
                                    0 0 80px -20px ${primaryColor}30`,
                    }}
                >
                    {/* Glowing border effect */}
                    <div
                        className="absolute -inset-[1px] rounded-3xl opacity-50 pointer-events-none"
                        style={{
                            background: `linear-gradient(135deg, ${primaryColor}40, transparent 40%, transparent 60%, ${secondaryColor}40)`,
                            animation: "glow 3s ease-in-out infinite",
                        }}
                    />

                    {/* Tag badge with glow */}
                    <span
                        className="inline-flex items-center gap-2 text-xs font-bold px-4 py-1.5 rounded-full mb-6 uppercase tracking-widest"
                        style={{
                            background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                            color: "white",
                            boxShadow: `0 0 30px ${primaryColor}50, 0 8px 25px -5px ${primaryColor}40`,
                        }}
                    >
                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                        <EditableText
                            value={content.tag || "NEW RELEASE"}
                            onChange={(val) => onContentChange?.({ ...content, tag: val })}
                            className="text-white"
                            as="span"
                            placeholder="Tag"
                        />
                    </span>

                    {/* Title - White text for dark bg */}
                    <h1
                        className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight tracking-tight text-white"
                        style={{ fontFamily }}
                    >
                        <EditableText
                            value={content.title || "Build Something Amazing"}
                            onChange={(val) => onContentChange?.({ ...content, title: val })}
                            className="inline text-white"
                            as="span"
                            placeholder="Main Title"
                        />
                        {content.highlightText && (
                            <>
                                {" "}
                                <span
                                    className="relative inline-block bg-clip-text text-transparent"
                                    style={{
                                        backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                                        textShadow: `0 0 40px ${primaryColor}40`,
                                    }}
                                >
                                    <EditableText
                                        value={content.highlightText}
                                        onChange={(val) =>
                                            onContentChange?.({ ...content, highlightText: val })
                                        }
                                        className="inline"
                                        as="span"
                                        placeholder="Highlight"
                                    />
                                </span>
                            </>
                        )}
                    </h1>

                    {/* Subtitle - Gray for dark bg */}
                    <p
                        className="text-lg text-gray-400 mb-8 max-w-xl mx-auto leading-relaxed"
                        style={{ fontFamily }}
                    >
                        <EditableText
                            value={
                                content.subtitle ||
                                "The modern platform for creators, developers, and dreamers. Start your journey now."
                            }
                            onChange={(val) =>
                                onContentChange?.({ ...content, subtitle: val })
                            }
                            className="text-gray-400"
                            as="span"
                            placeholder="Subtitle text..."
                        />
                    </p>

                    {/* CTA Buttons with glow */}
                    <div className="flex flex-wrap justify-center gap-4 mb-8">
                        <a
                            href={onContentChange ? undefined : (content.primaryCtaLink || "#")}
                            target={content.primaryCtaLink?.startsWith('http') && !onContentChange ? "_blank" : undefined}
                            rel={content.primaryCtaLink?.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                            onClick={(e) => onContentChange && e.preventDefault()}
                            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-white font-bold shadow-lg hover:scale-105 transition-all cursor-pointer"
                            style={{
                                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                                boxShadow: `0 0 35px ${primaryColor}40, 0 10px 30px -5px ${primaryColor}30`,
                            }}
                        >
                            <EditableText
                                value={content.primaryCta || "Start Free"}
                                onChange={(val) =>
                                    onContentChange?.({ ...content, primaryCta: val })
                                }
                                className="text-white"
                                as="span"
                                placeholder="Primary CTA"
                            />
                        </a>
                        <a
                            href={onContentChange ? undefined : (content.secondaryCtaLink || "#")}
                            target={content.secondaryCtaLink?.startsWith('http') && !onContentChange ? "_blank" : undefined}
                            rel={content.secondaryCtaLink?.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                            onClick={(e) => onContentChange && e.preventDefault()}
                            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold transition-all hover:scale-105 cursor-pointer"
                            style={{
                                background: "rgba(255,255,255,0.05)",
                                color: "white",
                                border: `1px solid rgba(255,255,255,0.15)`,
                            }}
                        >
                            <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <EditableText
                                value={content.secondaryCta || "Watch Demo"}
                                onChange={(val) =>
                                    onContentChange?.({ ...content, secondaryCta: val })
                                }
                                className="text-white"
                                as="span"
                                placeholder="Secondary CTA"
                            />
                        </a>
                    </div>

                    {/* Stats row - dark theme */}
                    <div className="pt-6 border-t border-white/10">
                        <div className="flex justify-center items-center gap-8 text-sm">
                            <div className="text-center">
                                <div
                                    className="font-bold text-xl"
                                    style={{ color: primaryColor }}
                                >
                                    10K+
                                </div>
                                <div className="text-gray-500 text-xs">Active Users</div>
                            </div>
                            <div className="w-px h-10 bg-white/10" />
                            <div className="text-center">
                                <div
                                    className="font-bold text-xl"
                                    style={{ color: primaryColor }}
                                >
                                    99.9%
                                </div>
                                <div className="text-gray-500 text-xs">Uptime</div>
                            </div>
                            <div className="w-px h-10 bg-white/10" />
                            <div className="text-center">
                                <div
                                    className="font-bold text-xl"
                                    style={{ color: primaryColor }}
                                >
                                    4.9★
                                </div>
                                <div className="text-gray-500 text-xs">Rating</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
